const _ = require("lodash");

const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const AT = require("../../core/vault/ActionTypes");
const ErrorUtil = require("../../../shared/ErrorUtil");

const CouponUtil = require("../../../shared/CouponUtil");
const OpsCart = require("./OpsCart");
const OpsCheckoutForm = require("./OpsCheckoutForm");
const OpsCartStorage = require("./OpsCartStorage");

const SAVE_THROTTLING_TIME = 10000; //Time in ms that backend saves are throttled to.
const SAVE_RETRY_DELAY = 5000; //Time in ms after a failed save before it gets retried

const computeBackendCart = function(cart) {
	return _.pick(cart, ["cartID", "items", "lastItemsUpdate"]);
	// lastItemsUpdate is not saved on the back end, but is in the payload to keep track of failed saves.
};

// Process the shipping info (address, method) one last time for any
// discrepancies before sending to the backend.
// Example: This can happen in the case that a user fills out the shipping form, but then
// decides to edit their cart and only buy digital items. The shipping info will
// still be in the vault state, so this function will rectify that.
const computeBackendShipping = function(cart, shippingInfo, isClassWallet) {
	if (!OpsCart.needsShipping(cart)) {
		// if a CW order is not shippable, remove the default shipping method but
		// keep the address for tax purposes
		if (isClassWallet) {
			return setIn(shippingInfo, "method", null);
		}
		// If shipping info is collected but is not needed, strip it out for normal orders and return
		return null;
	}

	if (!shippingInfo.method) {
		// When inserting into tables, method must be explicitly null if no method.
		return setIn(shippingInfo, "method", null);
	} else {
		return shippingInfo;
	}
};

//
// saveCart
//

const saveCartNow = function(cart, api) {
	api.saveCart(computeBackendCart(cart));
};

const setupThrottlerInVault = function(vault) {
	const {api, throttler} = vault;

	throttler.config("cart", {
		delay: SAVE_THROTTLING_TIME,
		run: cart => saveCartNow(cart, api),
	});
};

const startCartSaving = function(cart, storage, throttler, api) {
	OpsCartStorage.saveToStorage(cart, storage);
	if (cart.isDoingFirstSave) {
		//While waiting for the cartID on the first save or while verifying shipping,
		//set a flag that we need to save as soon as we get a response with the id.
		//Don't save now as we could create two separate carts with different ids.
		return setIn(cart, ["needsSave"], true);
	} else if (!cart.cartID && !cart.isDoingFirstSave) {
		//For new carts, save immediately to get a cartID and set the first save
		//flag so no more saves occur until there's an id.
		saveCartNow(cart, api);
		return setIn(cart, ["isDoingFirstSave"], true);
	} else {
		//Otherwise, do a normal throttled save.
		throttler.queue("cart", cart);
		return cart;
	}
};

const setSaveSuccess = function(cart, response, savedCart, throttler, storage) {
	const newCartData = {};
	newCartData.saveSuccessUpdateTime = savedCart.lastItemsUpdate;
	if (
		cart.savesFailing &&
		cart.saveSuccessUpdateTime >= cart.saveFailUpdateTime
	) {
		//Clear failing info if this successful save was after the most recent failing one
		newCartData.saveFailUpdateTime = 0;
		newCartData.savesFailing = false;
	}
	if (cart.isDoingFirstSave) {
		newCartData.cartID = parseInt(response.cartID, 10);
		newCartData.isDoingFirstSave = false;
		if (cart.needsSave) {
			newCartData.needsSave = false;
			throttler.queue("cart", cart);
		}
		cart = extendIn(cart, [], newCartData);
		OpsCartStorage.saveToStorage(cart, storage);
	} else {
		cart = extendIn(cart, [], newCartData);
	}
	return cart;
};

const unsetIDAndSave = function(cart, storage, throttler, api) {
	cart = setIn(cart, "cartID", null);
	cart = startCartSaving(cart, storage, throttler, api);
	return cart;
};

// TODO: should we just pass the vault to some of these? Being explicit mostly
// helps so you know what side effects a vault function can and can't trigger,
// which is meaningless with a list this long.
const setSaveFail = function(
	cart,
	error,
	savedCart,
	storage,
	throttler,
	api,
	emitEvent,
	dispatchAfter,
	clock
) {
	if (savedCart.lastItemsUpdate < cart.saveSuccessUpdateTime) {
		// A more recently updated cart has saved successfully; this fail can be ignored
	} else if (error === "E_CART_NOT_FOUND") {
		cart = unsetIDAndSave(cart, storage, throttler, api);
	} else if (error === "E_CHECKOUT_DUPLICATE") {
		// If the backend says the cart has been checked out, destroy it.
		cart = OpsCartStorage.destroyCart(cart, storage, clock.now());
		emitEvent("cartCheckedOut");
	} else {
		// Otherwise, set the failing flags and do a delayed retry.
		cart = extendIn(cart, [], {
			saveFailUpdateTime: savedCart.lastItemsUpdate,
			savesFailing: true,
		});
		if (error !== "E_TOO_MANY_REQUESTS") {
			dispatchAfter(
				{
					type: AT.RETRY_SAVE_CART,
				},
				SAVE_RETRY_DELAY
			);
		}
	}
	return cart;
};

const retrySave = function(cart, throttler, api) {
	if (cart.savesFailing) {
		// If the cart's saves are still failing (so no successful save has cleared
		// the savesFailing flag), another save is needed.
		if (cart.isDoingFirstSave) {
			// The first save has failed, do an immediate normal save
			saveCartNow(cart, api);
		} else {
			// Just trigger a throttled save to make sure another one occurs
			throttler.queue("cart", cart);
		}
	}
	return cart;
};

const doInitialSaveIfNeeded = function(cart, api) {
	if (cart.items.length && !cart.cartID) {
		// This cart hasn't been saved successfully to the backend yet, but has
		// items. Try saving.
		cart = setIn(cart, "isDoingFirstSave", true);
		saveCartNow(cart, api);
	}
	return cart;
};

//
// startPaypal
//

const startPaypal = function(cart, api, emitEvent, now) {
	if (cart.isStartingPaypal) {
		return cart;
	}
	api.startPaypal(computeBackendCart(cart));
	return setIn(cart, ["isStartingPaypal"], now);
};

const setStartPaypalSuccess = function(cart, response) {
	const newCartData = {};
	newCartData.isStartingPaypal = false;
	newCartData.paypalPaymentID = response.paymentID;
	return extendIn(cart, [], newCartData);
};

const setStartPaypalFail = function(cart, error, api, emitEvent, now) {
	const resetCart = setIn(cart, "isStartingPaypal", false);
	emitEvent("startPaypalFail", error);
	return resetCart;
};

//
// verifyNewAccount
//

const verifyNewAccount = function(cart, user, api, emitEvent, clock) {
	if (!OpsCart.isReadyToVerifyNewAccount(cart)) {
		emitEvent("verifyNewAccountNotReady");
		return cart;
	} else if (cart.accountExists) {
		emitEvent("accountAlreadyExists");
		return cart;
	} else if (OpsCart.isVerifyingAccount(cart, user)) {
		return cart;
	}
	const account = OpsCart.getFormSection(cart, "account");
	api.verifyNewAccount(account.email, account.password);
	return setIn(cart, ["isVerifyingAccount"], clock.now());
};

// Returns {cart, step}.
const setVerifyNewAccountSuccess = function(cart, response, emitEvent) {
	const newCartData = {};
	const nextStep = OpsCart.getNextStep(cart, "account");
	let step = null;
	newCartData.isVerifyingAccount = false;
	if (response.userExists) {
		newCartData.accountExists = true;
		emitEvent("accountAlreadyExists");
	} else {
		newCartData.accountVerified = true;
		step = nextStep;
	}
	return {
		cart: extendIn(cart, [], newCartData),
		step,
	};
};

// Returns {cart, step}
const setVerifyNewAccountFailure = function(cart, error, emitEvent, moreInfo) {
	let step = null;
	cart = setIn(cart, "isVerifyingAccount", false);
	if (error === "E_TOO_MANY_REQUESTS") {
		step = "cart";
	} else if (error === "E_INVALID_PASSWORD") {
		// This is kind of weird that we're sticking in a "manual"
		// validation error here after checking password on Platform.
		// But if we don't do this, we'll have to have our own version
		// of password check that mimics Platform, which seems
		// superfluous. Or, handle the error differently for some types
		// of invalid passwords than others, which is behaviorly very
		// weird.
		cart = extendIn(cart, ["form", "validation", "account", "errorTypes"], {
			password: {type: moreInfo},
		});
		emitEvent("verifyNewAccountNotReady");
	} else {
		emitEvent("showOfficeModal", "verifyNewAccountFail");
	}
	return {cart, step};
};

//
// getCoupon
//

const getCoupon = function(cart, couponCode, api) {
	if (cart.isGettingCoupon) {
		return cart;
	}
	const newCartData = {isGettingCoupon: true};
	if (!OpsCart.hasAlreadyPreparedOrder(cart)) {
		api.getCoupon(couponCode);
	} else {
		const addressInfo = OpsCart.getFormSection(
			cart,
			OpsCart.getPrepareAddressSection(cart)
		);
		api.getCouponWithTaxes({
			cart: computeBackendCart(cart),
			shippingAddress: addressInfo,
			couponCode,
			verifyAddress: OpsCart.needsToVerifyAddress(cart),
		});
	}
	return extendIn(cart, [], newCartData);
};

const setGetCouponSuccess = function(
	cart,
	isPrepareOrder,
	response,
	emitEvent
) {
	cart = setIn(cart, "isGettingCoupon", false);
	if (response.coupon) {
		const couponStatus = CouponUtil.appliesToCart(response.coupon, cart.items);
		if (!couponStatus.ok && couponStatus.error === "E_UNUSABLE_COUPON") {
			ErrorUtil.log(
				"E_UNUSABLE_COUPON",
				"Backend returned active coupon " +
					"that should be inactive (see CouponUtil.isUsableCoupon)",
				response.coupon
			);
			emitEvent("couponNotApplicable", couponStatus.error);
			cart = setIn(cart, "lastCouponCode", response.coupon.code);
		} else if (!couponStatus.ok) {
			emitEvent("couponNotApplicable", couponStatus.error);
			cart = setIn(cart, "lastCouponCode", response.coupon.code);
		} else {
			cart = OpsCart.setCoupon(cart, response.coupon);
			cart = setIn(cart, "lastCouponCode", null);
			emitEvent("couponSuccess");
		}
	} else {
		const endpoint = isPrepareOrder ? "store:prepareOrder" : "store:coupon:get";
		ErrorUtil.log(
			"E_NO_COUPON_RESPONSE",
			`Coupon getting endpoint ${endpoint} ` + `did not have coupon in response`
		);
	}
	if (isPrepareOrder) {
		// We ignore the step part of the return.
		cart = setPrepareOrderSuccess(cart, response).cart;
	}
	return cart;
};

// Returns {cart, step}
const setGetCouponFail = function(
	cart,
	isPrepareOrder,
	error,
	moreInfo,
	throttler,
	api,
	storage,
	emitEvent,
	clock
) {
	cart = setIn(cart, "isGettingCoupon", false);
	let step = null;
	if (error === "E_INVALID_COUPON") {
		emitEvent("couponInvalid", error);
	} else if (isPrepareOrder) {
		const poRes = handleCommonPrepareOrderErrors(
			cart,
			error,
			moreInfo,
			throttler,
			api,
			storage,
			emitEvent,
			clock
		);
		if (poRes) {
			// Get all the coupon UI out of the way for whatever error handling
			// already happened.
			emitEvent("couponClose");
			cart = poRes.cart;
			step = poRes.step;
		} else {
			emitEvent("couponFail", error);
		}
	} else {
		emitEvent("couponFail", error);
	}
	return {cart, step};
};

//
// prepareOrder
//

const doPrepareOrderCall = function(cart, api) {
	// for a CW cart, the USER'S address will always be collected as the shipping
	// address, even if their cart only contains digital items.
	// CW uses an invoicing system, and the CW HQ address is used for billing. The
	// customer's address is collected here as "shipping" for tax purposes, even
	// if they are only buying digital items.
	const addressInfo = OpsCart.getFormSection(
		cart,
		cart.isClassWalletFlow ? "shipping" : OpsCart.getPrepareAddressSection(cart)
	);
	api.prepareOrder({
		cart: computeBackendCart(cart),
		shippingAddress: addressInfo,
		couponCode: OpsCart.getCouponCode(cart),
		verifyAddress: OpsCart.needsToVerifyAddress(cart),
		classWallet: !!cart.isClassWalletFlow,
	});
};

// Shared between the getCoupon and the standard prepareOrder calls. Returns
// null if the error was unhandled.
const handleCommonPrepareOrderErrors = function(
	cart,
	error,
	moreInfo,
	throttler,
	api,
	storage,
	emitEvent,
	clock
) {
	let step = null;
	const now = clock.now();
	if (error === "E_TOO_MANY_REQUESTS") {
		step = "cart";
	} else if (error === "E_ADDRESS_NOT_VERIFIED") {
		const type = moreInfo.type || "shipping";
		emitEvent("checkoutFieldsError", type, {[type]: ["address"]});
		cart = setIn(cart, "form", f =>
			OpsCheckoutForm.setSectionInvalidAddress(f, type, true)
		);
	} else if (error === "E_CART_NOT_FOUND") {
		ErrorUtil.log(
			"E_CART_NOT_FOUND",
			"Frontend sent nonexistent cart ID to backend",
			{cartID: cart.cartID}
		);
		cart = unsetIDAndSave(cart, storage, throttler, api);
		emitEvent("cartNotFound");
	} else if (error === "E_CHECKOUT_DUPLICATE") {
		// If the backend says the cart has been checked out, destroy it and kick them to review cart page.
		cart = OpsCartStorage.destroyCart(cart, storage, now);
		step = "cart";
		emitEvent("placeOrderDuplicate");
	} else {
		return null;
	}
	return {cart, step};
};

const prepareOrder = function(cart, api, throttler, emitEvent, clock) {
	if (!OpsCart.isReadyToPrepareOrder(cart)) {
		emitEvent("prepareOrderNotReady");
		return cart;
	} else if (cart.isPreparingOrder || cart.isGettingCoupon) {
		return cart;
	}
	// Cancel any currently scheduled saves, since the cart will get saved
	// immediately during this endpoint
	throttler.cancel("cart", cart);
	doPrepareOrderCall(cart, api);
	// Set a flag so we know to not try to hit this endpoint again until we hear
	// back and not to save changes to the cart
	return setIn(cart, ["isPreparingOrder"], clock.now());
};

// Returns {cart, step}.
const setPrepareOrderSuccess = function(cart, response) {
	const newCartData = {};
	if (cart.isDoingFirstSave || response.cartID !== cart.cartID) {
		newCartData.cartID = parseInt(response.cartID, 10);
		newCartData.isDoingFirstSave = false;
	}
	if (response.address) {
		newCartData.form = OpsCheckoutForm.setVerifiedAddress(
			cart.form,
			"shipping",
			response.address
		);
		newCartData.form = OpsCheckoutForm.setSectionInvalidAddress(
			newCartData.form,
			"shipping",
			false
		);
	}
	const newTax = response.tax;
	if (newTax != null) {
		newCartData.prices = OpsCart.updateTax(cart.prices, newTax);
	}

	newCartData.isPreparingOrder = false;
	newCartData.addressVerified = true;
	const currentStep = OpsCart.getPrepareStep(cart);
	return {
		cart: extendIn(cart, [], newCartData),
		step: OpsCart.getNextStep(cart, currentStep),
	};
};

// Returns {cart, step}.
const setPrepareOrderFail = function(
	cart,
	error,
	moreInfo,
	throttler,
	api,
	storage,
	emitEvent,
	clock
) {
	let step = null;
	const now = clock.now();
	const prepareOrderTime = cart.isPreparingOrder;
	cart = setIn(cart, "isPreparingOrder", false);
	const commonRes = handleCommonPrepareOrderErrors(
		cart,
		error,
		moreInfo,
		throttler,
		api,
		storage,
		emitEvent,
		clock
	);
	if (commonRes) {
		cart = commonRes.cart;
		step = commonRes.step;
	} else if (error === "E_INVALID_COUPON") {
		cart = OpsCart.clearCoupon(cart, {noMemory: true});
		emitEvent("couponNoLongerValid");
	} else if (now - prepareOrderTime < 5000 && cart.prepareOrderFails < 1) {
		// Unrecognized error, it's been under 5 seconds, and we haven't retried yet.
		// Immediately retry.
		doPrepareOrderCall(cart, api);
		cart = setIn(cart, "prepareOrderFails", x => x + 1);
		cart = setIn(cart, "isPreparingOrder", true);
	} else if (
		error === "E_TAXCLOUD_ERROR" ||
		error === "E_COULD_NOT_GET_TAX_RATES"
	) {
		emitEvent("prepareOrderTaxCloudFail");
	} else if (error === "E_INVALID_ZIP_CODE") {
		emitEvent("prepareOrderZipCodeFail");
	} else {
		// Unrecognized error, and retry conditions not met.
		emitEvent("prepareOrderFail");
	}
	return {cart, step};
};

const wasPrepareOrderCancelled = function(cart, requestedAt) {
	return (
		requestedAt &&
		cart.lastCancelledPrepareOrder &&
		cart.lastCancelledPrepareOrder >= requestedAt
	);
};

const cancelPrepareOrder = function(cart, now) {
	return extendIn(cart, [], {
		isPreparingOrder: false,
		lastCancelledPrepareOrder: now,
	});
};

//
// placeOrder
//

// Helper for placeOrder.
const getAccountForPayload = function(cart) {
	const steps = OpsCart.getAllCheckoutSteps(cart);
	if (steps.indexOf("account") === -1) {
		return null;
	}
	const acc = OpsCart.getFormSection(cart, "account");
	if (acc.ownerID) {
		return {ownerID: acc.ownerID};
	} else {
		return {
			email: acc.email,
			password: acc.password,
			remember: acc.remember,
			platformUserID: acc.platformUserID,
			firstName: acc.firstName,
			lastName: acc.lastName,
		};
	}
};

const placeOrder = function(cart, api, emitEvent, now) {
	if (!OpsCart.isReadyToPlaceOrder(cart)) {
		emitEvent("placeOrderNotReady");
		return cart;
	} else if (cart.isPlacingOrder) {
		return cart;
	}
	const data = {
		cart: computeBackendCart(cart),
		couponCode: OpsCart.getCouponCode(cart),
		shipping: computeBackendShipping(
			cart,
			OpsCart.getFormSection(cart, "shipping")
		),
		account: getAccountForPayload(cart),
		billing: OpsCart.getFormSection(cart, "billing"),
		contact: OpsCart.getFormSection(cart, "contact"),
		creditCard: OpsCart.getFormSection(cart, "creditCard"),
		subscribe: !!cart.subscribeOnCheckout,
		prices: cart.prices,
		captchaResponse: cart.captchaResponse,
	};

	if (cart.onPaypalFlow) {
		if (OpsCart.needsShipping(cart)) {
			delete data.billing;
		}
		delete data.creditCard;
		data.paymentID = cart.paypalPaymentID;
		data.payerID = cart.paypalPayerID;
	}

	api.placeOrder(data);
	cart = setIn(cart, "isPlacingOrder", now);
	return cart;
};

const createClassWalletOrder = function(
	cart,
	api,
	emitEvent,
	now,
	classWalletSessionID
) {
	if (!OpsCart.isReadyToPlaceOrder(cart)) {
		emitEvent("placeOrderNotReady");
		return cart;
	} else if (cart.isPlacingOrder) {
		return cart;
	}
	const backEndCart = computeBackendCart(cart);

	// in a CW order, this will always get the shipping address, even if
	// needsShipping() is false
	const address = computeBackendShipping(
		cart,
		OpsCart.getFormSection(cart, "shipping"),
		cart.isClassWalletFlow
	);

	api.createClassWalletOrder({
		classWalletSessionID,
		cart: backEndCart,
		prices: cart.prices,
		address,
	});
	cart = setIn(cart, "isPlacingOrder", now);
	return cart;
};

// Returns {cart, step}.
const setPlaceOrderSuccess = function(cart, storage, now, orderID) {
	cart = setIn(cart, "isPlacingOrder", false);

	const lastFinishedCart = _.omit(cart, "lastFinishedCart");
	lastFinishedCart.orderID = orderID;

	cart = OpsCartStorage.destroyCart(cart, storage, now);
	cart = setIn(cart, "lastFinishedCart", lastFinishedCart);
	return {cart, step: "confirm"};
};

// Returns {cart, step}.
const setPlaceOrderFail = function(
	cart,
	error,
	moreInfo,
	throttler,
	api,
	storage,
	emitEvent,
	clock
) {
	const now = clock.now();
	cart = setIn(cart, "isPlacingOrder", false);
	const eInfo = _.extend({cartID: cart.cartID}, moreInfo); // Sent with ErrorUtil calls.
	let step = null;

	if (error === "E_TOO_MANY_REQUESTS") {
		step = "cart";
	} else if (error === "E_CART_NOT_FOUND") {
		ErrorUtil.log(
			"E_CART_NOT_FOUND",
			"Frontend sent nonexistent cart ID to backend",
			eInfo
		);
		step = "cart";
		cart = unsetIDAndSave(cart, storage, throttler, api);
		emitEvent("cartNotFound");
	} else if (error === "E_COUPON_OUT_OF_DATE") {
		cart = OpsCart.clearCoupon(cart);
		emitEvent("couponOutOfDate");
	} else if (error === "E_INVALID_COUPON") {
		cart = OpsCart.clearCoupon(cart, {noMemory: true});
		emitEvent("couponNoLongerValid");
	} else if (error === "E_CHECKOUT_COUPON_PRICE_MISMATCH") {
		cart = OpsCart.clearCoupon(cart, {noMemory: true});
		emitEvent("couponNoLongerValid");
	} else if (error === "E_CHECKOUT_IN_PROGRESS") {
		emitEvent("orderInProgress");
	} else if (error === "E_CHECKOUT_CART_MISMATCH") {
		cart = setIn(cart, "addressVerified", false);
		step = "cart";
		emitEvent("mustRedoCheckout");
	} else if (error === "E_CHECKOUT_PRICE_MISMATCH") {
		cart = setIn(cart, "addressVerified", false);
		//Set tax to null as that is the most likely cause of a price mismatch.
		//That way, the next prepare order will have to recalculate it.
		cart = setIn(cart, "prices", OpsCart.updateTax(cart.prices, null));
		step = "cart";
		emitEvent("mustRedoCheckoutPriceMismatch");
	} else if (error === "E_CHECKOUT_INVALID_SHIPPING") {
		ErrorUtil.log(
			"E_PLACE_ORDER_PREMATURE",
			"placeOrder endpoint called before shipping form completed",
			eInfo
		);
		step = "shipping";
		emitEvent("checkoutFieldsError", "shipping", null);
	} else if (error === "E_CHECKOUT_INVALID_BILLING") {
		ErrorUtil.log(
			"E_PLACE_ORDER_PREMATURE",
			"placeOrder endpoint called before billing form completed",
			eInfo
		);
		step = "billing";
		emitEvent("checkoutFieldsError", "billing", null);
	} else if (error === "E_CHECKOUT_INVALID_CONTACT") {
		ErrorUtil.log(
			"E_PLACE_ORDER_PREMATURE",
			"placeOrder endpoint called before contact form completed",
			eInfo
		);
		step = "billing";
		emitEvent("checkoutFieldsError", "billing", null);
	} else if (error === "E_ADDRESS_NOT_VERIFIED") {
		const type = moreInfo.type || "shipping";
		step = type;
		emitEvent("checkoutFieldsError", type, {[type]: ["address"]});
		cart = setIn(cart, "form", f =>
			OpsCheckoutForm.setSectionInvalidAddress(f, type, true)
		);
	} else if (error === "E_CHECKOUT_INPUT" && cart.onPaypalFlow) {
		step = "paypalinfo";
		let fields = null;
		const code = moreInfo && moreInfo.code;
		if (code === "INVALID_CITY_STATE_ZIP") {
			fields = {shipping: ["address"]};
		} else if (code === "PHONE_NUMBER_REQUIRED") {
			fields = {contact: ["phone"]};
		} else if (code === "SHIPPING_ADDRESS_INVALID") {
			fields = {shipping: ["address"]};
		}
		emitEvent("checkoutFieldsError", "paypalinfo", fields);
	} else if (error === "E_CHECKOUT_INPUT") {
		step =
			moreInfo.shipping && moreInfo.shipping.length ? "shipping" : "billing";
		emitEvent("checkoutFieldsError", step, moreInfo);
	} else if (error === "E_CHECKOUT_CARD_DECLINED") {
		step = "billing";
		emitEvent("cardDeclined");
	} else if (error === "E_INVALID_CARD") {
		step = "billing";
		emitEvent("cardInvalid");
	} else if (error === "E_CHECKOUT_DUPLICATE") {
		ErrorUtil.log(
			"E_PLACE_ORDER_DUPLICATE",
			"placeOrder endpoint called for cart that already ordered",
			eInfo
		);
		step = "cart";
		cart = OpsCartStorage.destroyCart(cart, storage, now);
		emitEvent("placeOrderDuplicate");
	} else if (error === "E_CHECKOUT_CRITICAL") {
		ErrorUtil.log(
			"E_PLACE_ORDER_CRITICAL",
			"placeOrder endpoint got E_CHECKOUT_CRITICAL back",
			eInfo
		);
		step = "cart";
		emitEvent("placeOrderCritical");
	} else if (error === "E_MULTIPLE_FAILED_CHECKOUTS") {
		emitEvent("multipleFailedCheckouts");
	} else if (error === "E_CHECKOUT_SERVER_TIMEOUT") {
		emitEvent("checkoutServerTimeout");
	} else if (error === "E_TAXCLOUD_ERROR") {
		emitEvent("placeOrderFail");
	} else if (error === "E_SUSPECTED_FRAUD") {
		emitEvent("placeOrderFail");
	} else if (error === "E_CHECKOUT_UNKNOWN") {
		emitEvent("placeOrderFail");
	} else if (error === "E_PAYPAL_UNKNOWN") {
		emitEvent("placeOrderFail");
	} else if (error === "E_PAYPAL_RESET") {
		step = "cart";
		cart = OpsCart.resetPaypal(cart);
		emitEvent("paypalReset", moreInfo && moreInfo.code);
	} else if (error === "E_PAYPAL_RETRY") {
		emitEvent("paypalRetry", moreInfo && moreInfo.code);
	} else if (error === "E_CVV_LENGTH_MISMATCH") {
		emitEvent("cvvMismatch");
	} else if (error === "E_CARD_TYPE_NOT_SUPPORTED") {
		emitEvent("cardTypeNotSupported");
	} else if (error === "E_ZIP_CODE_INVALID_CHAR") {
		emitEvent("zipCodeInvalidChar");
	} else if (_.get(moreInfo, "paymentNotStarted")) {
		// Unrecognized error, but backend says that payment steps did not start.
		emitEvent("placeOrderFail");
	} else {
		// Timeout or unrecognized error code.
		step = "cart";
		cart = OpsCartStorage.destroyCart(cart, storage, now);
		emitEvent("placeOrderUnknown");
	}

	return {cart, step};
};

module.exports = {
	setupThrottlerInVault,
	startCartSaving,
	setSaveSuccess,
	setSaveFail,
	retrySave,
	doInitialSaveIfNeeded,

	startPaypal,
	setStartPaypalSuccess,
	setStartPaypalFail,

	verifyNewAccount,
	setVerifyNewAccountSuccess,
	setVerifyNewAccountFailure,

	getCoupon,
	setGetCouponSuccess,
	setGetCouponFail,

	prepareOrder,
	setPrepareOrderSuccess,
	setPrepareOrderFail,
	cancelPrepareOrder,
	wasPrepareOrderCancelled,

	placeOrder,
	createClassWalletOrder,
	setPlaceOrderSuccess,
	setPlaceOrderFail,
};
